<script setup lang="ts">
import { useAppointmentRepository } from '@/repositories/appointmentRepo'
import { RecentAppointment } from '@/type/Appointment'
import { showNotify } from 'vant'
import { ref, watch } from 'vue'
import { useTranslation } from 'i18next-vue'
import { useAppStore } from '@/stores/AppStore'
import { getBranchDisplayNamei18n } from '@/utils/branch'

interface Props {
  show: boolean
  limit?: number
}
const props = defineProps<Props>()
interface EmitEvents {
  (e: 'onClose'): void
  (e: 'onCancel', appointment_uuid: string, branch_name: string, appoint_date: string, appoint_time: string): void
  (e: 'onPostpone', appointment_uuid: string): void
}
const emit = defineEmits<EmitEvents>()
const { t } = useTranslation()
const appStore = useAppStore()
const removeAppointment = (uuid: string) => {
  appointments.value = appointments.value.filter(apt => apt.uuid !== uuid)
}

const appointmentRepo = useAppointmentRepository()

const loading = ref(false)
const isError = ref(false)
const appointments = ref<RecentAppointment[]>([])
const DEFAULT_LIMIT = 5
const nextPageUrl = ref<string | null>(null)
const loadingMore = ref(false)
const isNoMore = ref(false)

const getLimitedRecentAppointments = async () => {
  try {
    loading.value = true
    const { data } = await appointmentRepo.fetchRecentAppointments(props.limit || DEFAULT_LIMIT)
    appointments.value = data.results
    nextPageUrl.value = data.next
    isNoMore.value = !data.next
  } catch (error) {
    isError.value = true
    showNotify({
      type: 'danger',
      message: t('appointment.error.fetch_upcoming'),
      duration: 5000,
    })
    console.error('Error fetching recent appointments:', error)
  } finally {
    loading.value = false
  }
}

const loadMore = async () => {
  if (!nextPageUrl.value || loadingMore.value) return

  try {
    loadingMore.value = true
    const urlObj = new URL(nextPageUrl.value)
    const pathWithQuery = urlObj.pathname + urlObj.search
    const { data } = await appointmentRepo.fetchMoreRecentAppointments(pathWithQuery)
    appointments.value = [...appointments.value, ...data.results]
    nextPageUrl.value = data.next
    isNoMore.value = !data.next
  } catch (error) {
    showNotify({
      type: 'danger',
      message: t('appointment.error.load_more'),
      duration: 5000,
    })
    console.error('Error loading more appointments:', error)
    isNoMore.value = true
  } finally {
    loadingMore.value = false
  }
}

const formatDate = (dateString: string) => {
  // Expecting format like "2025-10-20" and converting to "20/10/25"
  const [year, month, day] = dateString.split('-')
  return `${day}/${month}/${year.slice(-2)}`
}
const formatTime = (timeString: string) => {
  // Expecting format like "15:20:00" and converting to "15.20"
  return timeString.slice(0, 5).replace(':', '.')
}

const onCancelAppointment = async (
  appointment_uuid: string,
  branch_name: string,
  appoint_date: string,
  appoint_time: string,
) => {
  emit('onCancel', appointment_uuid, branch_name, appoint_date, appoint_time)
}
const onPostponeAppointment = (appointment_uuid: string) => {
  emit('onPostpone', appointment_uuid)
  emit('onClose')
}

defineExpose({
  removeAppointment,
  fetchAppointments: getLimitedRecentAppointments,
})

watch(
  () => props.show,
  newVal => {
    if (newVal && !appointments.value.length) {
      // fetch only first open
      getLimitedRecentAppointments()
    }
  },
)
</script>

<template>
  <van-popup :show="show" position="bottom" round :style="{ height: '70%' }" @click-overlay="emit('onClose')">
    <div class="tw-flex tw-flex-col tw-h-full">
      <!-- Header -->
      <div class="tw-p-4 tw-border-b">
        <div class="tw-flex tw-justify-between tw-items-center">
          <h2 class="tw-text-xl tw-font-bold">{{ t('appointment.recent_title') }}</h2>
          <van-icon name="cross" size="24" @click="emit('onClose')" class="tw-cursor-pointer" />
        </div>
      </div>

      <!-- Content -->
      <div class="tw-flex-1 tw-overflow-y-auto tw-p-4">
        <van-loading v-if="loading" class="tw-flex tw-justify-center tw-items-center tw-h-full" />

        <div v-else-if="isError" class="tw-flex tw-justify-center tw-items-center tw-h-full">
          <p class="tw-text-red-500">{{ t('appointment.error.load_data') }}</p>
        </div>

        <div v-else>
          <van-list
            :offset="50"
            :loading="loadingMore"
            :finished="isNoMore"
            :finished-text="t('appointment.no_more_appointments')"
            @load="loadMore"
          >
            <div class="tw-space-y-3">
              <div
                v-for="appointment in appointments"
                :key="appointment.uuid"
                class="tw-bg-white tw-rounded-lg tw-shadow tw-px-4 tw-py-2 tw-border tw-border-2 tw-border-[#c8c9cc]"
              >
                <div class="tw-flex tw-justify-between tw-items-start">
                  <div class="tw-flex-1">
                    <p class="tw-text-sm tw-text-gray-600">
                      {{ t('appointment.branch_label') }}
                      <span class="tw-font-semibold tw-text-gray-800">{{
                        getBranchDisplayNamei18n(appointment, appStore.language)
                      }}</span>
                    </p>
                    <p class="tw-text-sm tw-text-gray-600 tw-mt-1">
                      {{ t('appointment.date_label') }}
                      <span class="tw-font-semibold tw-text-gray-800">{{ formatDate(appointment.appoint_date) }}</span>
                    </p>
                    <p class="tw-text-sm tw-text-gray-600 tw-mt-1">
                      {{ t('appointment.time_label') }}
                      <span class="tw-font-semibold tw-text-gray-800">{{ formatTime(appointment.appoint_time) }}</span>
                    </p>
                  </div>
                  <div class="tw-flex tw-flex-col tw-gap-2">
                    <v-btn
                      class="tw-px-[30px] tw-text-lg"
                      color="yellow-darken-2"
                      size="small"
                      rounded
                      @click="onPostponeAppointment(appointment.uuid)"
                    >
                      {{ t('appointment.postpone') }}
                    </v-btn>
                    <v-btn
                      class="tw-px-[30px] tw-text-lg"
                      color="red"
                      size="small"
                      rounded
                      @click="
                        onCancelAppointment(
                          appointment.uuid,
                          getBranchDisplayNamei18n(appointment, appStore.language),
                          formatDate(appointment.appoint_date),
                          formatTime(appointment.appoint_time),
                        )
                      "
                    >
                      {{ t('cancel') }}
                    </v-btn>
                  </div>
                </div>
              </div>
            </div>
          </van-list>
        </div>
      </div>
    </div>
  </van-popup>
</template>
